import axios from 'axios';
import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { COMPANION_NAME } from './companion-config';
import { formatTimeAgo } from '@/i18n/intl';
import type { AnyTFunction } from '@/i18n/msg';

/**
 * tailwind-merge only knows the radius names shipped in its own default theme,
 * so the named rungs added for #635 (`tailwind.config.js` → borderRadius) are
 * invisible to it: `cn('rounded-card', 'rounded-full')` would keep BOTH and let
 * CSS source order decide, where the `rounded-[14px]` they replaced correctly
 * lost to `rounded-full`. Registering them restores that behaviour exactly.
 * Any radius rung added to the Tailwind config must be added here too.
 *
 * The SAME trap, worse, for the nine-rung type ladder (`fontSize` in the
 * Tailwind config, design-grammar §2). tailwind-merge does not just ignore an
 * unknown `text-*` rung — it mis-files it as a text COLOUR, because that is the
 * other thing `text-` prefixes. So `cn('text-label', 'text-stevens-ink-3')`
 * resolved to `text-stevens-ink-3` alone and the size was silently dropped.
 * Every rung paired with a colour was affected, `MonoLabel` on every call site
 * included: it has been emitting no font-size at all, inheriting whatever the
 * parent had, since the ladder landed. Nothing looked broken — text merely
 * rendered at the wrong size, which is exactly the drift the ladder exists to
 * stop. Found by StatusChip's typography test (#719); tracked as B-217.
 * Any rung added to the Tailwind config must be added here too.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: [{ rounded: ['sharp', 'card', 'editorial'] }],
      'font-size': [{
        text: [
          'stat', 'stat-sm', 'title', 'card-title', 'lede',
          'body', 'body-sm', 'caption', 'label', 'meta',
        ],
      }],
    },
  },
});

/**
 * Utility function to merge Tailwind CSS classes
 * Used extensively with shadcn/ui components
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractApiErrorMessage(error: unknown): string | undefined {
  return axios.isAxiosError(error)
    ? (error.response?.data as { message?: string })?.message
    : undefined;
}

/**
 * The toast description for a failed request, in the reader’s language
 * (#2109 R3-B11). The server’s messages are English and stay English on the
 * server, so only an `en` reader sees them: `en` returns
 * `extractApiErrorMessage(error)` exactly as today (possibly `undefined`),
 * and every other language gets the localized generic
 * `common:states.pleaseTryAgain`. Parent-facing call sites switch to this;
 * staff call sites keep `extractApiErrorMessage` until R4.
 */
export function localizedApiErrorMessage(
  error: unknown,
  t: AnyTFunction,
  language: string,
): string | undefined {
  if (language === 'en' || language.startsWith('en-')) return extractApiErrorMessage(error);
  return (t as unknown as (key: string) => string)('common:states.pleaseTryAgain');
}

/** Read a Blob as text. `Blob.prototype.text()` where it exists (every browser
 *  we target), FileReader where it does not — which includes jsdom, the unit
 *  test environment, so a test may drive this path rather than mock past it. */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('unreadable error body'));
    reader.readAsText(blob);
  });
}

/**
 * #1632 / B-584 — make a `responseType: 'blob'` failure readable.
 *
 * Axios applies the response type to the ERROR response as well as the
 * successful one, so a JSON refusal from a blob-typed request arrives with
 * `error.response.data` **being a Blob** rather than the parsed body. Every
 * reader downstream — `extractApiErrorMessage` above, and therefore the toast
 * the person sees — looks for `data.message` and finds nothing, so a refusal
 * that took the trouble to name its reason is rendered as a generic retry.
 *
 * This decodes that body in place and hands the SAME error back, so the caller
 * still rethrows a real `AxiosError` with its status and config intact. A body
 * that is not JSON (an nginx error page, say) is left alone: the honest thing to
 * say about a response that names no reason is nothing.
 *
 * Anything that is not an axios error, or whose data is already parsed, is
 * returned untouched — so it is safe to route every blob download through it.
 */
export async function decodeBlobErrorBody(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError(error) || !error.response) return error;
  const { data } = error.response;
  if (!(data instanceof Blob)) return error;
  try {
    error.response.data = JSON.parse(await readBlobText(data));
  } catch {
    // Not JSON, or unreadable. Leave the blob where it is rather than inventing
    // a message; the caller's own fallback line is the truthful one here.
  }
  return error;
}

/** Extract the first name from a persona name like "Maya - The Practical Helper". */
export function personaFirstName(raw: string | undefined | null): string {
  const name = raw ?? COMPANION_NAME;
  const idx = name.indexOf(' - ');
  return idx !== -1 ? name.slice(0, idx).trim() : name;
}

export function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export const GREETING_PREFIX: Record<string, string> = {
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
};

/**
 * Compact relative time string (e.g. "just now", "3m ago", "2h ago", "5d ago").
 * Falls back to the numeric date for anything older than 7 days.
 *
 * #2109 R3 L0: through Intl (`formatRelativeCompact`) and the `common`
 * catalog, in the UI language. The units still FLOOR (90 minutes is "1h
 * ago"), and en output is byte-identical to the hand-built strings it replaced.
 * An unparseable value returns `''` instead of throwing a RangeError.
 *
 * @deprecated #3168 — a thin wrapper over `formatTimeAgo` (`i18n/intl.ts`,
 * the compact style), the one relative-time formatter; new code calls that.
 */
export function timeAgo(dateString: string): string {
  return formatTimeAgo(dateString, { style: 'compact' });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/** Sentence-case a raw enum for a label or chip: `in_progress` → "In progress".
 *  Labels no longer uppercase via CSS (design-grammar §2 v3.1), so a raw enum
 *  would otherwise show its underscores. Prefer a `*-display.ts` table when one
 *  exists; this is the floor, not the vocabulary. */
export function enumLabel(value: string | null | undefined): string {
  if (!value) return '';
  const s = String(value).replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
